<vs-app-page
  *vsLet="meta$ | async as meta"
  class="!pt-0 !px-0 [&_.vs-app-page-header]:!mb-3 [&_.vs-app-page-header]:!px-1"
  [config]="{
    title: 'Emails',
    description: 'Manage and view all emails sent by the platform.',
    isSubPage: true
  }"
>
  <vs-badge
    *vsLet="(meta?.totalItems ?? null) as totalItems"
    slot="title-end"
    [class.italic]="totalItems === null"
    [class.animate-pulse]="totalItems === null"
  >
    {{totalItems === null ? 'loading' : (totalItems | number) + ' ' + (totalItems === 1 ? 'email' : 'emails')}}
  </vs-badge>

  <div class="flex gap-2 justify-between items-center flex-wrap mt-1 mb-3">
    <div class="ml-auto flex items-start gap-2 py-1.5 sm:py-0 w-full sm:w-auto">
      <button
        #filtersTrigger="cdkOverlayOrigin"
        cdkOverlayOrigin
        vs-button
        appearance="outline"
        type="button"
        (click)="$event.preventDefault(); $event.stopImmediatePropagation(); toggleFiltersOverlay()"
      >
        <vs-icon name="sliders-04" slot="start"></vs-icon>
        Filters
        <vs-badge *ngIf="activeFilterCount > 0" class="ml-1" theme="default">{{ activeFilterCount }}</vs-badge>
      </button>

      <ng-template
        cdkConnectedOverlay
        [cdkConnectedOverlayOrigin]="filtersTrigger"
        [cdkConnectedOverlayOpen]="isFiltersOverlayOpen"
        [cdkConnectedOverlayPositions]="positions"
        [cdkConnectedOverlayHasBackdrop]="true"
        cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
        (backdropClick)="closeFiltersOverlay()"
        (detach)="closeFiltersOverlay()"
      >
        <vs-card class="my-2 !rounded-md !shadow-md" [style.maxWidth]="'min(90vw, 320px)'">
          <div class="p-3 w-[min(90vw,320px)] max-h-[70vh] overflow-y-auto" (click)="$event.stopPropagation()">
            <div class="flex items-center justify-between mb-2">
              <p class="text-sm font-semibold text-themed-900">Filters</p>
              <button vs-button appearance="clear" size="sm" type="button" (click)="clearAllFilters()">
                Clear all
              </button>
            </div>

            <div class="flex flex-col gap-3">
              <div class="flex flex-col gap-1">
                <p class="text-xs font-medium text-gray-700">Status</p>
                <vs-select
                  vsInput
                  [formControl]="statusesControl"
                  [config]="{ multiple: true, placeholder: 'All statuses' }"
                >
                  <li *ngFor="let option of statusOptions" vs-select-option [value]="option.value">
                    {{ option.label }}
                  </li>
                </vs-select>
              </div>

              <div class="flex flex-col gap-1">
                <p class="text-xs font-medium text-gray-700">Sent date</p>
                <button
                  type="button"
                  class="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white w-full hover:border-gray-400 transition-colors"
                  [vsMenuTriggerFor]="dateRangeMenu"
                >
                  <vs-icon name="calendar" class="text-gray-400" size="16"></vs-icon>
                  <div class="flex-1 text-left text-sm">
                    <ng-container *ngIf="startDateControl.value || endDateControl.value; else noDateRange">
                      <span class="text-gray-900">
                        {{ startDateControl.value ? (startDateControl.value | date: 'MMM d') : 'Start' }} - {{
                        endDateControl.value ? (endDateControl.value | date: 'MMM d, y') : 'End' }}
                      </span>
                    </ng-container>
                    <ng-template #noDateRange>
                      <span class="text-gray-500">Select date range</span>
                    </ng-template>
                  </div>
                  <vs-icon name="chevron-down" class="text-gray-400" size="14"></vs-icon>
                </button>

                <ng-template #dateRangeMenu>
                  <vs-menu class="!z-50">
                    <div class="p-4">
                      <vs-date-picker
                        [range]="true"
                        [selectCurrentDayByDefault]="false"
                        (selectionChanged)="onDateRangeChange($event)"
                      ></vs-date-picker>
                    </div>
                  </vs-menu>
                </ng-template>
              </div>
            </div>
          </div>
        </vs-card>
      </ng-template>

      <vs-input-container class="min-w-[200px] sm:max-w-[212px] w-full sm:w-auto ml-auto">
        <vs-icon name="search-lg" slot="start"></vs-icon>
        <input type="text" placeholder="Recipient or subject..." [formControl]="searchFormControl" />
      </vs-input-container>
    </div>
  </div>

  <div class="rounded-xl overflow-hidden border border-gray-300 shadow-sm">
    <div *vsLet="loading$ | async as loading" class="relative">
      <vs-table
        [columns]="tableColumns"
        [data]="(items$ | async)"
        [class.pointer-events-none]="loading"
        (rowClicked)="navigateTo($event.id)"
      ></vs-table>

      <div *ngIf="loading" class="absolute inset-0 bg-gray-50/25 z-20 overflow-hidden">
        <div class="relative w-full h-[3px] mt-[38px]">
          <div class="absolute inset-0 bg-primary-100"></div>
          <div id="indeterminate-progress-bar-value" class="absolute w-full h-full bg-primary-400"></div>
        </div>
      </div>
    </div>

    <vs-pagination
      [config]="paginationConfig$ | async"
      (pagination)="pagination$.next({ page: $event.currentPage, limit: $event.itemsPerPage })"
    ></vs-pagination>
  </div>
</vs-app-page>

<ng-template #sentOnTooltip let-local="local" let-utc="utc">
  <div class="max-w-sm">
    <p class="text-xs font-semibold mb-0.5 text-gray-700">Sent on</p>
    <p class="text-xs font-medium mb-3 text-gray-500">{{ local }}</p>

    <p class="text-xs italic font-semibold mb-0.5 text-gray-500">UTC</p>
    <p class="text-xs italic font-medium text-gray-500">{{ utc }}</p>
  </div>
</ng-template>

<ng-template #baseTooltip let-title="title" let-subtitle="subtitle">
  <div class="max-w-sm">
    <p class="text-xs font-semibold mb-0.5 text-gray-700">{{ title }}</p>
    <p class="text-xs font-medium text-gray-500">{{ subtitle }}</p>
  </div>
</ng-template>

<ng-template #statusTooltip let-context>
  <div class="max-w-sm">
    <p class="text-xs font-semibold mb-0.5 text-gray-700">{{ context.text }}</p>
    <p class="text-xs font-medium mb-0.5 text-gray-500">{{ context.timestamp }}</p>
    <p class="text-xs font-medium text-gray-500">{{ context.description }}</p>
  </div>
</ng-template>
